import axios from 'axios';
import type { SignedAssetResponse } from '../types/Shared';
import type { SignAssetPayload, TdmAssetPayloadType } from '../types/Tdm';
import { baseUrl, HttpMethods } from './base';

export async function getTask(taskId: number) {
  const resp = await axios.get(`${baseUrl.SHERPA}/tasks/${taskId}`);
  return resp.data;
}

export async function getTdmsByProjectPhase({
  type,
  phase,
  page,
  perPage,
  projectId,
}: {
  type: string;
  phase: string | undefined;
  page: number;
  perPage: number;
  projectId: number;
}) {
  const resp = await axios.get(
    `${baseUrl.SHERPA}/${type}?orderBy=sortIndex&order=asc&perPage=${perPage}&page=${page}&phase=${phase}&project_id=${projectId}`
  );
  return resp.data;
}

export async function createTask(data: object) {
  const resp = await axios.post(`${baseUrl.SHERPA}/tasks`, data);
  return resp.data;
}

export async function updateTask(taskId: number, data: object) {
  const resp = await axios.patch(`${baseUrl.SHERPA}/tasks/${taskId}`, data);
  return resp.data;
}

export async function deleteTask(taskId: number) {
  const resp = await axios.delete(`${baseUrl.SHERPA}/tasks/${taskId}`);
  return resp.data;
}

export async function getDeliverable(deliverableId: number) {
  const resp = await axios.get(`${baseUrl.SHERPA}/deliverables/${deliverableId}`);
  return resp.data;
}

export async function createDeliverable(data: object) {
  const resp = await axios.post(`${baseUrl.SHERPA}/deliverables`, data);
  return resp.data;
}

export async function updateDeliverable(deliverableId: number, data: object) {
  const resp = await axios.patch(`${baseUrl.SHERPA}/deliverables/${deliverableId}`, data);
  return resp.data;
}

export async function deleteDeliverable(deliverableId: number) {
  const resp = await axios.delete(`${baseUrl.SHERPA}/deliverables/${deliverableId}`);
  return resp.data;
}

export async function getMilestone(milestoneId: number) {
  const resp = await axios.get(`${baseUrl.SHERPA}/milestones/${milestoneId}`);
  return resp.data;
}

export async function createMilestone(data: object) {
  const resp = await axios.post(`${baseUrl.SHERPA}/milestones`, data);
  return resp.data;
}

export async function updateMilestone(milestoneId: number, data: object) {
  const resp = await axios.patch(`${baseUrl.SHERPA}/milestones/${milestoneId}`, data);
  return resp.data;
}

export async function deleteMilestone(milestoneId: number) {
  const resp = await axios.delete(`${baseUrl.SHERPA}/milestones/${milestoneId}`);
  return resp.data;
}

export async function createTdm({ type, payload }: { type: string; payload: any }) {
  const resp = await axios.post(`${baseUrl.SHERPA}/${type}`, payload);
  return resp.data;
}

export async function updateTdmStatus({ type, tdmId, payload }: { type: string; tdmId: number; payload: { status: string } }) {
  const resp = await axios.patch(`${baseUrl.SHERPA}/${type}/${tdmId}`, payload);
  return resp.data;
}

export interface UpdateTdmPayloadInterface {
  name: string;
  category: string;
  categoryId?: number;
  status: string;
  leadId: number;
  supportId: number;
  notes: string;
  vendorId: number;
  dueDate: string;
  startDate: string;
}

interface UpdateTdmPayloadService {
  type: string;
  tdmId: number;
  data: Partial<UpdateTdmPayloadInterface>;
}

export async function updateTdm({ type, tdmId, data }: UpdateTdmPayloadService) {
  const resp = await axios({
    method: HttpMethods.PATCH,
    url: `${baseUrl.SHERPA}/${type}/${tdmId}`,
    data,
  });
  return resp.data;
}

export async function deleteTdm({ type, tdmId }: { type: string; tdmId: number }) {
  const resp = await axios.delete(`${baseUrl.SHERPA}/${type}/${tdmId}`);
  return resp.data;
}

interface uploadTdmAssetService {
  tdmId: number;
  tdmType: string;
  assetType: string;
  file: TdmAssetPayloadType;
}

export async function uploadTdmAsset(data: uploadTdmAssetService) {
  const res = await axios({
    method: HttpMethods.POST,
    url: `${baseUrl.SHERPA}/assets/tdm`,
    data: { ...data, assetType: `${data.assetType}File` },
  });
  return res.data;
}

export interface DeleteTdmAssetPayload {
  id: number;
  tdmType: string;
  key: string;
  assetType: string;
}

export async function deleteTdmAsset({ assetId }: { assetId: number }) {
  const resp = await axios({
    method: HttpMethods.DELETE,
    url: `${baseUrl.SHERPA}/assets/tdm/${assetId}`,
  });
  return resp.data;
}

export const signTdmFile = async (payload: SignAssetPayload) => {
  const { tdmId, ...rest } = payload;
  const response = await axios.request<SignedAssetResponse>({
    method: HttpMethods.POST,
    url: `${baseUrl.SHERPA}/assets/upload/tdm/${tdmId}/sign`,
    data: rest,
  });
  return response.data;
};
